import { useState, useEffect, useMemo, useRef } from 'react';
import api from '../api/axios';

const fmtINR = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');

const waLink = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return null;
  return `https://wa.me/${digits.length === 10 ? '91' + digits : digits}`;
};

const toISODate = (d) => {
  if (!d) return '';
  const str = String(d);
  if (/^\d{4}-\d{2}-\d{2}/.test(str)) return str.slice(0, 10);
  const dt = new Date(str);
  if (isNaN(dt)) return '';
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
};

const PILL = 'inline-flex items-center justify-center gap-1 whitespace-nowrap px-3.5 py-1.5 rounded-full text-xs font-bold transition';
const PILL_GREEN = `${PILL} bg-[#ECFDF5] text-[#10B981] hover:bg-emerald-100`;
const PILL_TEAL = `${PILL} bg-teal-50 text-teal-600 hover:bg-teal-100`;
const PILL_DISABLED = `${PILL} bg-gray-50 text-gray-300 cursor-default`;

const FIELD = 'w-full border border-gray-200 bg-gray-50/60 rounded-xl px-3 py-2.5 text-sm transition focus:outline-none focus:bg-white focus:ring-2 focus:ring-purple-400 focus:border-transparent';
const LABEL = 'block text-[11px] font-semibold uppercase tracking-wide text-gray-500 mb-1';

const TIME_LEFT_OPTIONS = [
  { value: 'today', label: 'Expires today' },
  { value: 'le1', label: '1 day or less' },
  { value: 'le3', label: 'Up to 3 days' },
  { value: '4to6', label: '4 – 6 days' },
];

const matchesTimeLeft = (days, filter) => {
  if (!filter) return true;
  const d = Number(days);
  if (filter === 'today') return d <= 0;
  if (filter === 'le1') return d <= 1;
  if (filter === 'le3') return d <= 3;
  if (filter === '4to6') return d >= 4;
  return true;
};

function SearchableSelect({ value, onChange, options, placeholder, allLabel }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef(null);

  useEffect(() => {
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? options.filter((o) => o.toLowerCase().includes(q)) : options;
  }, [options, query]);

  return (
    <div className="relative" ref={wrapRef}>
      <input
        type="text"
        placeholder={placeholder}
        value={open ? query : value}
        onFocus={() => { setOpen(true); setQuery(''); }}
        onClick={() => setOpen(true)}
        onChange={(e) => { setOpen(true); setQuery(e.target.value); }}
        className={`${FIELD} pr-8`}
      />
      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs">▾</span>
      {open && (
        <div className="absolute z-30 mt-1 w-full min-w-[200px] max-h-64 overflow-y-auto bg-white border border-gray-100 rounded-xl shadow-lg">
          <button type="button" onClick={() => { onChange(''); setOpen(false); }}
            className="block w-full text-left px-3 py-2 text-sm text-gray-500 hover:bg-purple-50 hover:text-purple-600">
            {allLabel}
          </button>
          {filtered.length === 0 && <div className="px-3 py-2 text-sm text-gray-400">No matches</div>}
          {filtered.map((o) => (
            <button type="button" key={o} onClick={() => { onChange(o); setOpen(false); }}
              className={`block w-full text-left px-3 py-2 text-sm hover:bg-purple-50 hover:text-purple-600 ${o === value ? 'bg-purple-50 text-purple-700 font-semibold' : ''}`}>
              {o}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const cls = status === 'active' ? 'bg-green-50 text-green-600'
    : status === 'expired' ? 'bg-gray-100 text-gray-500' : 'bg-red-50 text-red-500';
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${cls}`}>{status}</span>;
}

function DaysLeftBadge({ days }) {
  const cls = days <= 1 ? 'bg-red-50 text-red-600' : days <= 3 ? 'bg-orange-50 text-orange-600' : 'bg-yellow-50 text-yellow-700';
  const text = days <= 0 ? 'Expires today' : days === 1 ? '1 day left' : `${days} days left`;
  return <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${cls}`}>{text}</span>;
}

// mode: 'all' (Subscriptions) or 'expiring' (Expiring Plans)
function AdminSubscriptionsPanel({ mode = 'all' }) {
  const expiring = mode === 'expiring';
  const [status, setStatus] = useState('loading');
  const [rows, setRows] = useState([]);

  const [search, setSearch] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [agentFilter, setAgentFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [cycleFilter, setCycleFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [autoPayFilter, setAutoPayFilter] = useState('');
  const [timeLeftFilter, setTimeLeftFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const load = async () => {
    setStatus('loading');
    try {
      const res = expiring
        ? await api.get('/admin/expiring-plans', { params: { days: 6 } })
        : await api.get('/admin/subscriptions');
      setRows(res.data.subscriptions || []);
      setStatus('ok');
    } catch (err) {
      console.error('Failed to load', err);
      setStatus('error');
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mode]);

  const uniq = (key) => Array.from(new Set(rows.map((r) => r[key]).filter(Boolean))).sort();
  const companyOptions = useMemo(() => uniq('employer_name'), [rows]); // eslint-disable-line
  const agentOptions = useMemo(() => uniq('agent_name'), [rows]); // eslint-disable-line
  const planOptions = useMemo(() => uniq('plan_name'), [rows]); // eslint-disable-line
  const cycleOptions = useMemo(() => uniq('billing_cycle'), [rows]); // eslint-disable-line
  const statusOptions = useMemo(() => uniq('status'), [rows]); // eslint-disable-line

  const filtered = rows.filter((s) => {
    const q = search.trim().toLowerCase();
    const okSearch = !q ||
      s.employer_name?.toLowerCase().includes(q) ||
      s.plan_name?.toLowerCase().includes(q) ||
      s.agent_name?.toLowerCase().includes(q) ||
      s.email?.toLowerCase().includes(q) ||
      s.phone?.includes(q);
    const exp = toISODate(s.expires_at);
    const okDate = (!dateFrom || (exp && exp >= dateFrom)) && (!dateTo || (exp && exp <= dateTo));
    return okSearch && okDate &&
      (!companyFilter || s.employer_name === companyFilter) &&
      (!agentFilter || s.agent_name === agentFilter) &&
      (!planFilter || s.plan_name === planFilter) &&
      (!cycleFilter || s.billing_cycle === cycleFilter) &&
      (!statusFilter || s.status === statusFilter) &&
      (!autoPayFilter || (autoPayFilter === 'on' ? !!s.auto_renew : !s.auto_renew)) &&
      matchesTimeLeft(s.days_left, timeLeftFilter);
  });

  const hasFilters = search || companyFilter || agentFilter || planFilter || cycleFilter ||
    statusFilter || autoPayFilter || timeLeftFilter || dateFrom || dateTo;

  const clearAll = () => {
    setSearch(''); setCompanyFilter(''); setAgentFilter(''); setPlanFilter(''); setCycleFilter('');
    setStatusFilter(''); setAutoPayFilter(''); setTimeLeftFilter(''); setDateFrom(''); setDateTo('');
  };

  const colCount = expiring ? 11 : 11;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-700 to-pink-600 bg-clip-text text-transparent">
          {expiring ? 'Expiring Plans' : 'Employer Subscriptions'}
        </h2>
        <p className="text-gray-500 text-sm mt-1">
          {expiring
            ? 'All employers whose plan expires within the next 6 days.'
            : 'Billing status for all employers.'}
        </p>
      </div>

      {status === 'error' && (
        <div className="bg-red-50 border border-red-100 text-red-600 text-sm rounded-xl p-4">
          Could not load data. <button onClick={load} className="underline font-medium">Retry</button>
        </div>
      )}

      {status === 'ok' && rows.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4">
          <div className="relative mb-4">
            <span className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">🔍</span>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by employer, agent, plan, email or phone..." className={`${FIELD} pl-10 pr-10`} />
            {search && (
              <button type="button" onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-purple-600 text-lg leading-none">×</button>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-3">
            <div>
              <label className={LABEL}>Company</label>
              <SearchableSelect value={companyFilter} onChange={setCompanyFilter} options={companyOptions}
                placeholder="All Companies" allLabel="All Companies" />
            </div>
            <div>
              <label className={LABEL}>Agent</label>
              <SearchableSelect value={agentFilter} onChange={setAgentFilter} options={agentOptions}
                placeholder="All Agents" allLabel="All Agents" />
            </div>
            <div>
              <label className={LABEL}>Plan</label>
              <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)} className={`${FIELD} cursor-pointer capitalize`}>
                <option value="">All Plans</option>
                {planOptions.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
            <div>
              <label className={LABEL}>Cycle</label>
              <select value={cycleFilter} onChange={(e) => setCycleFilter(e.target.value)} className={`${FIELD} cursor-pointer capitalize`}>
                <option value="">All Cycles</option>
                {cycleOptions.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>

            {expiring ? (
              <>
                <div>
                  <label className={LABEL}>Auto-pay</label>
                  <select value={autoPayFilter} onChange={(e) => setAutoPayFilter(e.target.value)} className={`${FIELD} cursor-pointer`}>
                    <option value="">All</option>
                    <option value="on">On</option>
                    <option value="off">Off</option>
                  </select>
                </div>
                <div>
                  <label className={LABEL}>Time Left</label>
                  <select value={timeLeftFilter} onChange={(e) => setTimeLeftFilter(e.target.value)} className={`${FIELD} cursor-pointer`}>
                    <option value="">Any</option>
                    {TIME_LEFT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </>
            ) : (
              <div>
                <label className={LABEL}>Status</label>
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={`${FIELD} cursor-pointer capitalize`}>
                  <option value="">All Statuses</option>
                  {statusOptions.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}

            <div>
              <label className={LABEL}>Expires From</label>
              <input type="date" value={dateFrom} max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)} className={FIELD} />
            </div>
            <div>
              <label className={LABEL}>Expires To</label>
              <input type="date" value={dateTo} min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)} className={FIELD} />
            </div>
          </div>

          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
            <span>Showing <b className="text-purple-700">{filtered.length}</b> of {rows.length} {expiring ? 'plans' : 'subscriptions'}</span>
            {hasFilters && (
              <button type="button" onClick={clearAll} className="font-semibold text-purple-600 hover:underline">Clear all</button>
            )}
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[1500px]">
            <thead>
              <tr className="text-left text-gray-400 text-[10px] uppercase bg-gray-50/60">
                {!expiring && <th className="px-5 pt-4 pb-2 font-semibold">ID</th>}
                <th className="px-5 pt-4 pb-2 font-semibold">Employer</th>
                <th className="px-5 pt-4 pb-2 font-semibold">Agent</th>
                <th className="px-5 pt-4 pb-2 font-semibold">Email</th>
                <th className="px-5 pt-4 pb-2 font-semibold">Phone</th>
                <th className="px-5 pt-4 pb-2 font-semibold">Plan</th>
                <th className="px-5 pt-4 pb-2 font-semibold">Amount</th>
                <th className="px-5 pt-4 pb-2 font-semibold">Cycle</th>
                {expiring && <th className="px-5 pt-4 pb-2 font-semibold">Auto-pay</th>}
                <th className="px-5 pt-4 pb-2 font-semibold">Expires</th>
                <th className="px-5 pt-4 pb-2 font-semibold sticky z-20 bg-gray-50 w-[140px] min-w-[140px]" style={{ right: 250 }}>
                  {expiring ? 'Time left' : 'Status'}
                </th>
                <th className="px-5 pt-4 pb-2 font-semibold text-right sticky right-0 z-20 bg-gray-50 border-l border-gray-100 w-[250px] min-w-[250px] shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {status === 'loading' && (
                <tr><td colSpan={colCount} className="py-6 text-center text-gray-400">Loading…</td></tr>
              )}
              {status === 'ok' && rows.length === 0 && (
                <tr><td colSpan={colCount} className="py-6 text-center text-gray-400">
                  {expiring ? 'No plans are expiring in the next 6 days.' : 'No subscriptions found.'}
                </td></tr>
              )}
              {status === 'ok' && rows.length > 0 && filtered.length === 0 && (
                <tr><td colSpan={colCount} className="py-6 text-center text-gray-400">No results match your search or filters.</td></tr>
              )}
              {filtered.map((s) => (
                <tr key={s.id} className="border-t border-gray-50 hover:bg-purple-50/30 transition-colors">
                  {!expiring && <td className="px-5 py-2.5 text-gray-500">#{s.id}</td>}
                  <td className="px-5 py-2.5 font-medium text-gray-700">{s.employer_name}</td>
                  <td className="px-5 py-2.5">{s.agent_name || <span className="text-gray-400">Unassigned</span>}</td>
                  <td className="px-5 py-2.5">
                    {s.email ? <a href={`mailto:${s.email}`} className="text-purple-600 hover:underline">{s.email}</a> : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-5 py-2.5 whitespace-nowrap">
                    {s.phone ? <a href={`tel:${s.phone}`} className="text-purple-600 hover:underline">{s.phone}</a> : <span className="text-gray-400">—</span>}
                  </td>
                  <td className="px-5 py-2.5 capitalize">{s.plan_name}</td>
                  <td className="px-5 py-2.5">{fmtINR(s.amount)}</td>
                  <td className="px-5 py-2.5 capitalize">{s.billing_cycle ?? '—'}</td>
                  {expiring && (
                    <td className="px-5 py-2.5">
                      {s.auto_renew
                        ? <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-green-50 text-green-600">On</span>
                        : <span className="text-xs font-semibold px-2.5 py-1 rounded-full bg-gray-100 text-gray-500">Off</span>}
                    </td>
                  )}
                  <td className="px-5 py-2.5 text-gray-500 whitespace-nowrap">{s.expires_at}</td>
                  <td className="px-5 py-2.5 sticky z-10 bg-white w-[140px] min-w-[140px]" style={{ right: 250 }}>
                    {expiring ? <DaysLeftBadge days={s.days_left} /> : <StatusBadge status={s.status} />}
                  </td>
                  <td className="px-5 py-2.5 sticky right-0 z-10 bg-white border-l border-gray-100 w-[250px] min-w-[250px] shadow-[-6px_0_8px_-4px_rgba(0,0,0,0.08)]">
                    <div className="flex items-center justify-end gap-2 whitespace-nowrap">
                      {s.phone ? <a href={`tel:${s.phone}`} className={PILL_GREEN}>📞 Call</a> : <span className={PILL_DISABLED}>📞 Call</span>}
                      {waLink(s.phone)
                        ? <a href={waLink(s.phone)} target="_blank" rel="noreferrer" className={PILL_TEAL}>💬 WhatsApp</a>
                        : <span className={PILL_DISABLED}>💬 WhatsApp</span>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default AdminSubscriptionsPanel;